import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Modal, { Field, useModalClose } from './Modal';
import DataTable from './DataTable';
import EmptyState from './EmptyState';
import { Chip } from './StatusChip';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { dueStatus } from '../utils/compliance';
import { formatDate, parseUkDate, plural } from '../utils/format';
import { presetsFor } from '../utils/propertyPresets';
import { assetPath, normaliseAssetKind } from '../utils/assetLinks';
import { printAssetLabels } from './AssetQrLabels';

/**
 * <AssetRegister /> — the un-tagged ("non-interactive") building mode.
 *
 * The most contested discussion of session 2. Josh's model is built around NFC
 * tags: the tag is the key that opens the asset's data. Harvey and Dave pushed
 * back — an inspector routinely arrives at a site with no tags at all (the client
 * won't pay for them on a first visit, a new instruction lands with no lead time,
 * or the job IS the survey to decide whether tags are worth it). Their position
 * was blunt: without this they can't use CloudSafe day to day.
 *
 * Where it landed: the contractor pre-loads the asset list at the desk — typed in
 * or pasted straight out of Excel — and works against that. The one thing that
 * matters is the asset reference. Use the same numbering convention the tags will
 * carry, and when they're finally fitted the manual history lines up with the
 * tagged asset instead of being re-keyed. That's what "Adopt" does here.
 */

const box = { border: '1px solid var(--cw-border)', borderRadius: 12, background: 'var(--cw-surface)' };
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: 'var(--cs-text-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--cw-text-muted)', borderBottom: '1px solid var(--cw-border)', whiteSpace: 'nowrap' };
const td = { padding: '9px 10px', fontSize: 'var(--cs-text-sm)', color: 'var(--cw-text)', borderBottom: '1px solid var(--cw-border-light)', verticalAlign: 'top' };
const dash = <span style={{ color: 'var(--cw-placeholder)' }} aria-label="Not recorded">—</span>;

const EMPTY = { asset_ref: '', name: '', asset_type: '', location: '', floor: '', make: '', model: '', serial: '', install_date: '', last_inspected: '', next_due: '', quantity: '', notes: '' };
const CONDITION_TONE = { Good: 'success', Attention: 'warning', Defect: 'danger' };

/** Column headings we recognise in a pasted sheet, however people label them. */
const HEADER_MAP = [
  ['asset_ref', /^(asset\s*)?(ref|reference|id|no|number|tag|tag\s*(id|no|ref))\.?$/i],
  ['name', /^(name|description|asset|asset\s*name|item)$/i],
  ['asset_type', /^(type|asset\s*type|category|kind)$/i],
  ['location', /^(location|position|area|where|location\s*description)$/i],
  ['floor', /^(floor|level|storey|story)$/i],
  ['make', /^(make|manufacturer|brand)$/i],
  ['model', /^(model|model\s*no\.?)$/i],
  ['serial', /^(serial|serial\s*(no|number)\.?|s\/n)$/i],
  ['install_date', /^(install(ed|ation)?\s*date|installed|date\s*installed|fitted)$/i],
  ['last_inspected', /^(last\s*(inspect(ed|ion)|test(ed)?|service)d?\s*(date)?|previous\s*inspection)$/i],
  ['next_due', /^(next\s*(due|inspection|test|service)(\s*date)?|due(\s*date)?|re-?inspection\s*date)$/i],
  ['quantity', /^(qty|quantity|count|no\.\s*off)$/i],
  ['notes', /^(notes?|comments?|remarks?)$/i],
];

const matchHeader = (h) => {
  const clean = String(h || '').trim();
  const hit = HEADER_MAP.find(([, re]) => re.test(clean));
  return hit ? hit[0] : null;
};

/**
 * Turn a block pasted out of Excel into rows.
 * Tab-separated is what a real Excel copy produces; comma-separated covers a CSV
 * someone opened in a text editor. Quoted commas are respected in the CSV case.
 */
export function parsePastedAssets(text) {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n').filter((l) => l.trim());
  if (!lines.length) return { rows: [], columns: [], unmapped: [] };
  const delim = lines[0].includes('\t') ? '\t' : ',';
  const split = (line) => {
    if (delim === '\t') return line.split('\t');
    const out = []; let cur = ''; let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
      else if (c === ',' && !q) { out.push(cur); cur = ''; }
      else cur += c;
    }
    out.push(cur);
    return out;
  };

  const head = split(lines[0]).map((h) => h.trim());
  const mapped = head.map(matchHeader);
  // No recognisable headings? Assume the first two columns are reference and name —
  // that is what a hand-typed list looks like, and the preview lets them see it.
  const hasHeader = mapped.some(Boolean);
  const cols = hasHeader ? mapped : head.map((_, i) => (i === 0 ? 'asset_ref' : i === 1 ? 'name' : null));
  const body = hasHeader ? lines.slice(1) : lines;

  const rows = body.map((line) => {
    const cells = split(line);
    const row = { extra: {} };
    cells.forEach((cell, i) => {
      const v = String(cell || '').trim().replace(/^"|"$/g, '');
      if (!v) return;
      if (cols[i] === 'next_due') row.extra.next_due = v;   // stored with the asset's extra data
      else if (cols[i]) row[cols[i]] = v;
      else if (hasHeader && head[i]) row.extra[head[i]] = v;   // keep unrecognised columns
    });
    if (!Object.keys(row.extra).length) delete row.extra;
    return row;
  }).filter((r) => r.asset_ref || r.name);

  const unmapped = hasHeader ? head.filter((h, i) => h && !cols[i]) : [];
  return { rows, columns: cols, unmapped, headers: head, hasHeader };
}

/** Next-due date for a manual asset (kept in its extra data), as YYYY-MM-DD or ''. */
const nextDueOf = (a) => {
  const v = a?.extra?.next_due;
  if (!v) return '';
  const iso = String(v).match(/^\d{4}-\d{2}-\d{2}/) ? String(v).slice(0, 10) : parseUkDate(String(v));
  return iso || '';
};

const DueChip = ({ date }) => {
  const s = dueStatus(date);
  return <Chip tone={s.tone} label={s.label} />;
};

/**
 * <AssetRegister /> — the ONE asset register for a building.
 *
 * - Your register: manual (un-tagged) assets, stored via /api/contractor/manual-assets.
 * - On the CloudSafe record: tagged anchor points / fixed equipment for a CloudSafe
 *   property (`recordAssets`, from the property record), read-only.
 *
 * Each row carries a next-due date and a status in words (Overdue / Due soon / In date).
 */
const AssetRegister = ({ propertyId, localPropertyId, propertyName, recordAssets = null }) => {
  const { confirm } = useConfirm();
  const [data, setData] = useState({ assets: [], tagged: [], mode: 'empty' });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [q, setQ] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [initialForm, setInitialForm] = useState(EMPTY);
  const [formError, setFormError] = useState(null);
  const [qtyError, setQtyError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);     // the asset being edited
  const [importOpen, setImportOpen] = useState(false);
  const [paste, setPaste] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showTagged, setShowTagged] = useState(false);

  const scopeQuery = propertyId ? `property_id=${propertyId}` : `local_property_id=${localPropertyId}`;
  const scopeBody = propertyId ? { property_id: propertyId } : { local_property_id: localPropertyId };

  const load = useCallback(async () => {
    if (!propertyId && !localPropertyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await apiFetch(`/api/contractor/manual-assets?${scopeQuery}`);
      setData({ assets: r?.assets || [], tagged: r?.tagged || [], mode: r?.mode || 'empty' });
      setLoadError(null);
    } catch (e) { setLoadError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, localPropertyId]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data.assets;
    return data.assets.filter((a) => [a.asset_ref, a.name, a.asset_type, a.location, a.floor, a.make, a.model, a.serial]
      .some((v) => String(v || '').toLowerCase().includes(needle)));
  }, [data.assets, q]);

  const adoptable = useMemo(() => data.assets.filter((a) => a.adoptable), [data.assets]);
  // With the record's equipment passed in we show the full tagged table below; without it,
  // fall back to the manual-assets API's list of tagged refs not yet on the register.
  const untaggedOnRecord = useMemo(() => data.tagged.filter((t) => !t.listed), [data.tagged]);
  const dueCounts = useMemo(() => {
    const c = { overdue: 0, soon: 0 };
    data.assets.forEach((a) => { const l = dueStatus(nextDueOf(a)).level; if (l === 'overdue') c.overdue += 1; else if (l === 'soon') c.soon += 1; });
    (recordAssets || []).forEach((a) => { const l = dueStatus(a.due).level; if (l === 'overdue') c.overdue += 1; else if (l === 'soon') c.soon += 1; });
    return c;
  }, [data.assets, recordAssets]);

  const closeForm = () => { setAddOpen(false); setEditing(null); setForm(EMPTY); setInitialForm(EMPTY); setFormError(null); setQtyError(null); };
  const dirty = JSON.stringify(form) !== JSON.stringify(initialForm);

  const save = async () => {
    if (!form.asset_ref.trim()) { setFormError('Enter an asset reference — it links this record to its tag later'); return; }
    if (form.quantity !== '' && (!Number.isInteger(Number(form.quantity)) || Number(form.quantity) < 1)) { setQtyError('Enter a whole number of 1 or more'); return; }
    setQtyError(null);
    setSaving(true);
    const { next_due: nextDue, ...fields } = form;
    try {
      if (editing) {
        // next_due is merged into the asset's extra data server-side; other extra columns are kept.
        await apiFetch(`/api/contractor/manual-assets/${editing.id}`, { method: 'PUT', body: JSON.stringify({ ...fields, next_due: nextDue }) });
        toast.success(`${fields.asset_ref.trim()} updated`);
      } else {
        const extra = nextDue ? { next_due: nextDue } : undefined;
        await apiFetch('/api/contractor/manual-assets', { method: 'POST', body: JSON.stringify({ ...scopeBody, ...fields, extra }) });
        toast.success(`${fields.asset_ref.trim()} added to the register`);
      }
      closeForm(); load();
    } catch (e) {
      if (e?.status === 409) setFormError(apiErrorMessage(e));
      else toast.error(`Couldn't save the asset: ${apiErrorMessage(e)}`);
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    const label = a.asset_ref || a.name || 'this asset';
    const ok = await confirm({
      title: `Remove ${label} from the register?`,
      body: a.inspections
        ? `${plural(a.inspections, 'inspection')} recorded against it will no longer be linked to an asset.`
        : 'It will be removed from this building’s asset register.',
      confirmLabel: 'Remove asset', tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/contractor/manual-assets/${a.id}`, { method: 'DELETE' });
      toast.success(`${label} removed`); load();
    } catch (e) {
      if (e?.status === 409) {
        const force = await confirm({
          title: 'This asset has inspection history',
          body: `${e.body?.message || 'Inspections have been recorded against this asset.'} Deleting it leaves that history without an asset.`,
          confirmLabel: 'Delete anyway', tone: 'danger',
        });
        if (!force) return;
        try { await apiFetch(`/api/contractor/manual-assets/${a.id}?force=1`, { method: 'DELETE' }); toast.success(`${label} removed`); load(); }
        catch (err) { toast.error(`Couldn't remove the asset: ${apiErrorMessage(err)}`); }
      } else toast.error(`Couldn't remove the asset: ${apiErrorMessage(e)}`);
    }
  };

  const adopt = async (a) => {
    try {
      const r = await apiFetch(`/api/contractor/manual-assets/${a.id}/link`, {
        method: 'POST',
        body: JSON.stringify({ kind: a.adoptable.kind, tagged_asset_id: a.adoptable.id }),
      });
      toast.success(r?.inspections_carried
        ? `${a.asset_ref} linked to its tag — ${plural(r.inspections_carried, 'inspection')} carried across`
        : `${a.asset_ref} linked to its tag`);
      load();
    } catch (e) { toast.error(`Couldn't link the asset: ${apiErrorMessage(e)}`); }
  };

  const preview = useMemo(() => (paste.trim() ? parsePastedAssets(paste) : null), [paste]);

  const runImport = async () => {
    if (!preview?.rows.length) return;
    setImporting(true);
    try {
      const r = await apiFetch('/api/contractor/manual-assets/import', {
        method: 'POST',
        body: JSON.stringify({ ...scopeBody, assets: preview.rows }),
      });
      // Keep the dialog open with the outcome — skipped rows need reading, not a fleeting toast.
      setImportResult({ created: r?.created || 0, updated: r?.updated || 0, skipped: r?.skipped || [] });
      load();
    } catch (e) { toast.error(`Couldn't import the list: ${apiErrorMessage(e)}`); }
    finally { setImporting(false); }
  };
  // A sheet of QR labels for what's shown: your register (as filtered) + tagged assets on the record.
  const printLabels = () => printAssetLabels([
    ...filtered.map((a) => ({ kind: 'manual', id: a.id, ref: a.asset_ref, name: a.name, location: [a.floor, a.location].filter(Boolean).join(' · '), property_name: propertyName })),
    ...(recordAssets || []).filter((a) => normaliseAssetKind(a.assetType) && a.assetId)
      .map((a) => ({ kind: normaliseAssetKind(a.assetType), id: a.assetId, ref: a.system || a.category, name: a.category, location: a.location, property_name: propertyName })),
  ], { heading: propertyName ? `${propertyName} — asset labels` : 'Asset labels' });

  const closeImport = () => { setImportOpen(false); setPaste(''); setImportResult(null); };

  const openAdd = () => { setEditing(null); setForm(EMPTY); setInitialForm(EMPTY); setFormError(null); setAddOpen(true); };
  const openEdit = (a) => {
    const f = {
      asset_ref: a.asset_ref || '', name: a.name || '', asset_type: a.asset_type || '',
      location: a.location || '', floor: a.floor || '', make: a.make || '', model: a.model || '',
      serial: a.serial || '', install_date: (a.install_date || '').slice(0, 10),
      last_inspected: (a.last_inspected || '').slice(0, 10), next_due: nextDueOf(a),
      quantity: a.quantity == null ? '' : String(a.quantity), notes: a.notes || '',
    };
    setEditing(a); setForm(f); setInitialForm(f); setFormError(null); setAddOpen(true);
  };

  const setF = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const field = (label, key, props = {}, extra = {}) => (
    <Field label={label} required={extra.required} error={extra.error} hint={extra.hint}>
      <input value={form[key]} onChange={setF(key)} {...props} />
    </Field>
  );

  const columns = [
    { key: 'asset_ref', label: 'Reference', primary: true, render: (a) => (
      <span style={{ ...mono, fontWeight: 700, fontSize: 'var(--cs-text-sm)' }}>
        <Link to={assetPath('manual', a.id)} aria-label={`Open ${a.asset_ref} — schedules, files, risk and history`}>{a.asset_ref}</Link>
        {a.tagged && <Chip tone="success" label="Tagged" style={{ marginLeft: 7 }} title="Linked to its NFC tag" />}
      </span>
    ) },
    { key: 'name', label: 'Asset', render: (a) => a.name || dash },
    { key: 'asset_type', label: 'Type', render: (a) => a.asset_type || dash },
    { key: 'location', label: 'Location', sortValue: (a) => [a.floor, a.location].filter(Boolean).join(' '), render: (a) => [...new Set([a.floor, a.location].filter(Boolean))].join(' · ') || dash },
    { key: 'last_inspected', label: 'Last inspected', render: (a) => (a.last_inspected ? formatDate(a.last_inspected) : dash) },
    { key: 'next_due', label: 'Next due', sortValue: (a) => nextDueOf(a), render: (a) => (nextDueOf(a) ? formatDate(nextDueOf(a)) : dash) },
    { key: 'status', label: 'Status', sortValue: (a) => dueStatus(nextDueOf(a)).days ?? 99999, render: (a) => <DueChip date={nextDueOf(a)} /> },
    { key: 'inspections', label: 'Inspections', align: 'center', mobileHide: true, render: (a) => a.inspections || dash },
    { key: 'actions', label: '', sortable: false, align: 'right', render: (a) => (
      <span style={{ display: 'inline-flex', gap: 6, whiteSpace: 'nowrap' }}>
        <button type="button" className="cw-btn cw-btn-secondary" onClick={() => openEdit(a)} aria-label={`Edit ${a.asset_ref}`}>Edit</button>
        <button type="button" className="cw-btn cw-btn-danger-outline" onClick={() => remove(a)} aria-label={`Remove ${a.asset_ref}`}>Remove</button>
      </span>
    ) },
  ];

  const recordColumns = [
    { key: 'system', label: 'Asset', primary: true, render: (a) => (normaliseAssetKind(a.assetType) && a.assetId
      ? <Link to={assetPath(a.assetType, a.assetId)} style={{ fontWeight: 700 }}>{a.system || a.category || 'Asset'}</Link>
      : <strong>{a.system || a.category || 'Asset'}</strong>) },
    { key: 'category', label: 'Category', render: (a) => a.category || dash },
    { key: 'location', label: 'Location', render: (a) => a.location || dash },
    { key: 'condition', label: 'Condition', render: (a) => (a.condition ? <Chip tone={CONDITION_TONE[a.condition] || 'muted'} label={a.condition} dot /> : dash) },
    { key: 'lastTested', label: 'Last tested', render: (a) => (a.lastTested ? formatDate(a.lastTested) : dash) },
    { key: 'due', label: 'Next due', render: (a) => (a.due ? formatDate(a.due) : dash) },
    { key: 'status', label: 'Status', sortValue: (a) => dueStatus(a.due).days ?? 99999, render: (a) => <DueChip date={a.due} /> },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* What this is, and why the reference matters. */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 'var(--cs-text-sm)', color: 'var(--cs-blue-dark, #1565C0)', background: 'var(--cw-blue-tint)', border: '1px solid var(--cs-blue-light, #bfdbfe)', borderRadius: 10, padding: '11px 14px', lineHeight: 1.5 }}>
        <i className="fa-solid fa-circle-info" style={{ marginTop: 2 }} aria-hidden="true" />
        <span>
          One register for {propertyName || 'this building'}: tagged assets from the CloudSafe record, plus anything you list
          before it has NFC tags. <strong>Use the numbering convention the tags will carry</strong> — when they’re fitted,
          each record links to its tag and the history you’ve already collected carries over.
        </span>
      </div>

      {(dueCounts.overdue > 0 || dueCounts.soon > 0) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} aria-live="polite">
          {dueCounts.overdue > 0 && <Chip tone="danger" label={`${plural(dueCounts.overdue, 'asset')} overdue`} dot />}
          {dueCounts.soon > 0 && <Chip tone="warning" label={`${plural(dueCounts.soon, 'asset')} due soon`} dot />}
        </div>
      )}

      {/* Adoption prompts — the moment the two worlds meet. */}
      {adoptable.length > 0 && (
        <div style={{ background: 'var(--cw-success-tint)', border: '1px solid var(--cs-green-border, #bbf7d0)', borderRadius: 10, padding: '11px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--cw-success-fg)', fontWeight: 700, fontSize: 'var(--cs-text-sm)' }}>
            <i className="fa-solid fa-link" aria-hidden="true" />
            {adoptable.length} asset{adoptable.length === 1 ? ' has' : 's have'} now been tagged
          </div>
          <div style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-success-fg)', margin: '5px 0 9px' }}>
            The reference matches a tagged asset on the CloudSafe record. Link them and the manual history follows the tag.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {adoptable.map((a) => (
              <button type="button" key={a.id} className="cw-btn cw-btn-secondary" onClick={() => adopt(a)}>
                <i className="fa-solid fa-link" aria-hidden="true" /> Link {a.asset_ref}
              </button>
            ))}
          </div>
        </div>
      )}

      {recordAssets && (
        <div className="cw-card" style={{ overflow: 'hidden' }}>
          <div className="cw-card-head">
            <div>
              <h3>On the CloudSafe record <Chip tone="info" label="From CloudSafe · read-only" style={{ marginLeft: 6 }} /></h3>
              <span className="cw-meta">{plural(recordAssets.length, 'tagged asset')} — anchor points and fixed equipment</span>
            </div>
          </div>
          <DataTable columns={recordColumns} rows={recordAssets} minWidth={760}
            getRowKey={(a, i) => `${a.assetType || 'asset'}-${a.assetId ?? i}`}
            getRowLabel={(a) => a.system || a.category || 'Asset'}
            emptyState={<EmptyState compact icon="fa-solid fa-tag" title="No tagged assets on the CloudSafe record" body="Assets tagged on CloudSafe for this property appear here automatically." />} />
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 0 }}>
          <label htmlFor="cw-asset-search" className="cw-sr-only">Search your asset register</label>
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--cw-placeholder)' }} />
          <input id="cw-asset-search" type="search" className="cw-input" style={{ paddingLeft: 30 }} placeholder="Search reference, type, location…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button type="button" className="cw-btn cw-btn-secondary" onClick={printLabels} disabled={!filtered.length && !(recordAssets || []).length}>
          <i className="fa-solid fa-qrcode" aria-hidden="true" /> Print QR labels
        </button>
        <button type="button" className="cw-btn cw-btn-secondary" onClick={() => setImportOpen(true)}>
          <i className="fa-solid fa-file-excel" aria-hidden="true" /> Paste from Excel
        </button>
        <button type="button" className="cw-btn cw-btn-primary" onClick={openAdd}>
          <i className="fa-solid fa-plus" aria-hidden="true" /> Add asset
        </button>
      </div>

      {/* Your register */}
      <div className="cw-card" style={{ overflow: 'hidden' }}>
        <div className="cw-card-head">
          <div>
            <h3>Your register</h3>
            <span className="cw-meta">{loading ? 'Loading…' : `${plural(data.assets.length, 'asset')} listed by your company`}</span>
          </div>
        </div>
        <DataTable
          columns={columns}
          rows={filtered}
          minWidth={900}
          loading={loading}
          error={loadError}
          onRetry={load}
          errorThing="the asset register"
          getRowKey={(a) => a.id}
          getRowLabel={(a) => `${a.asset_ref}${a.name ? ` ${a.name}` : ''}`}
          emptyState={data.assets.length ? (
            <EmptyState compact icon="fa-solid fa-magnifying-glass" title={`Nothing matches “${q}”`} body="Try a different reference, type or location." />
          ) : (
            <EmptyState compact icon="fa-solid fa-clipboard-list" title="No assets listed yet"
              body="Paste the site’s asset list out of Excel, or add them one at a time."
              action={<button type="button" className="cw-btn cw-btn-primary" onClick={() => setImportOpen(true)}><i className="fa-solid fa-file-excel" aria-hidden="true" /> Paste from Excel</button>} />
          )}
        />
      </div>

      {/* Tagged refs the API knows about but which aren't on your register (no record list passed). */}
      {!recordAssets && untaggedOnRecord.length > 0 && (
        <div style={{ ...box, padding: '11px 14px' }}>
          <button type="button" aria-expanded={showTagged}
            onClick={() => setShowTagged((v) => !v)}
            className="cw-btn-link"
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: 'var(--cw-text-2)' }}
          >
            <i className={`fa-solid fa-chevron-${showTagged ? 'down' : 'right'}`} aria-hidden="true" />
            {untaggedOnRecord.length} tagged asset{untaggedOnRecord.length === 1 ? '' : 's'} on the CloudSafe record aren’t on your register
          </button>
          {showTagged && (
            <ul style={{ margin: '10px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li className="cw-meta">These already have tags, so they don’t need listing here — shown so you can check nothing’s been missed.</li>
              {untaggedOnRecord.map((t) => (
                <li key={`${t.kind}-${t.id}`} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 'var(--cs-text-sm)', padding: '5px 0', borderTop: '1px solid var(--cw-border-light)', flexWrap: 'wrap' }}>
                  <span style={{ ...mono, fontWeight: 700 }}>{t.asset_ref}</span>
                  <span className="cw-meta">{t.asset_type}</span>
                  <span className="cw-meta" style={{ marginLeft: 'auto' }}>{t.location}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Add / edit */}
      {addOpen && (
        <Modal
          title={editing ? `Edit ${editing.asset_ref}` : 'Add asset'} maxWidth={620}
          dirty={dirty}
          onClose={closeForm}
          footer={<>
            <CancelButton disabled={saving} />
            <button type="button" className="cw-btn cw-btn-primary" onClick={save} disabled={saving} aria-busy={saving || undefined}>
              <i className={`fa-solid ${saving ? 'fa-spinner fa-spin' : 'fa-check'}`} aria-hidden="true" /> {saving ? 'Saving…' : editing ? 'Save changes' : 'Add asset'}
            </button>
          </>}
        >
          <div style={{ display: 'grid', gap: 12 }}>
            {field('Asset reference', 'asset_ref', { placeholder: 'e.g. VP46-AP-FX-SF-SS-001', autoFocus: true, style: mono },
              { required: true, error: formError, hint: 'Match the convention the tags will use — this is what links the record to its tag later.' })}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 12 }}>
              {field('Asset name', 'name', { placeholder: 'e.g. Fire alarm panel, ground floor' })}
              <Field label="Type">
                <input value={form.asset_type} onChange={setF('asset_type')} list="cw-asset-types" placeholder="e.g. Emergency lighting" />
              </Field>
              {field('Location', 'location', { placeholder: 'e.g. Roof, north-west corner' })}
              {field('Floor / level', 'floor', { placeholder: 'e.g. Roof' })}
              {field('Make', 'make')}
              {field('Model', 'model')}
              {field('Serial number', 'serial')}
              {field('Quantity', 'quantity', { type: 'number', min: 1, step: 1, inputMode: 'numeric' }, { error: qtyError })}
              {field('Installed', 'install_date', { type: 'date' })}
              {field('Last inspected', 'last_inspected', { type: 'date' })}
              {field('Next due', 'next_due', { type: 'date' }, { hint: 'Next inspection, test or service — drives the status column.' })}
            </div>
            <datalist id="cw-asset-types">
              {presetsFor('equipmentCategory').map((o) => <option key={o} value={o} />)}
            </datalist>
            <Field label="Notes">
              <textarea style={{ minHeight: 64, resize: 'vertical' }} value={form.notes} onChange={setF('notes')} />
            </Field>
          </div>
        </Modal>
      )}

      {/* Excel paste */}
      {importOpen && (
        <Modal
          title="Paste the asset list from Excel" maxWidth={720}
          dirty={!!paste.trim() && !importResult}
          onClose={closeImport}
          footer={importResult ? (
            <button type="button" className="cw-btn cw-btn-primary" onClick={closeImport}>Done</button>
          ) : (<>
            <CancelButton disabled={importing} />
            <button type="button" className="cw-btn cw-btn-primary" onClick={runImport} disabled={importing || !preview?.rows.length} aria-busy={importing || undefined}>
              <i className={`fa-solid ${importing ? 'fa-spinner fa-spin' : 'fa-file-import'}`} aria-hidden="true" />
              {importing ? ' Importing…' : ` Import ${plural(preview?.rows.length || 0, 'asset')}`}
            </button>
          </>)}
        >
          {importResult ? (
            <div role="status" style={{ display: 'grid', gap: 10 }}>
              <p style={{ margin: 0, fontWeight: 700 }}>
                {[importResult.created ? `${importResult.created} added` : null, importResult.updated ? `${importResult.updated} updated` : null, importResult.skipped.length ? `${importResult.skipped.length} skipped` : null].filter(Boolean).join(' · ') || 'Nothing changed'}
              </p>
              {importResult.skipped.length > 0 && (
                <div style={{ ...box, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 12px', background: 'var(--cw-surface-alt)', borderBottom: '1px solid var(--cw-border)', fontWeight: 700, fontSize: 'var(--cs-text-sm)' }}>Rows not imported</div>
                  <ul style={{ margin: 0, padding: '8px 12px 8px 28px', maxHeight: 200, overflow: 'auto', fontSize: 'var(--cs-text-sm)' }}>
                    {importResult.skipped.map((sk, i) => <li key={i}><strong>{sk.row}</strong> — {sk.reason}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              <p style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-text-2)', lineHeight: 1.5, margin: 0 }}>
                Select the rows in your spreadsheet (including the heading row) and paste them below. Columns are matched
                by their heading — reference, name, type, location, floor, make, model, serial, installed, last inspected,
                next due, quantity, notes. Anything else is kept against the asset. Pasting a corrected sheet again
                <strong> updates</strong> the matching references rather than duplicating them.
              </p>
              <Field label="Pasted rows">
                <textarea
                  style={{ minHeight: 150, ...mono, resize: 'vertical' }}
                  placeholder={'Reference\tName\tType\tLocation\nVP46-AP-001\tRoof eyebolt A\tEyebolt\tNorth parapet'}
                  value={paste} onChange={(e) => setPaste(e.target.value)} autoFocus
                />
              </Field>
              {preview && (
                preview.rows.length ? (
                  <div style={{ ...box, overflow: 'hidden' }}>
                    <div style={{ padding: '8px 12px', background: 'var(--cw-surface-alt)', borderBottom: '1px solid var(--cw-border)', fontSize: 'var(--cs-text-sm)', fontWeight: 700, color: 'var(--cw-text-2)' }}>
                      {plural(preview.rows.length, 'row')} recognised
                      {!preview.hasHeader && <span style={{ fontWeight: 400, color: 'var(--cw-warning-fg)' }}> · no heading row found, reading column 1 as the reference and column 2 as the name</span>}
                      {preview.unmapped?.length > 0 && <span style={{ fontWeight: 400, color: 'var(--cw-text-muted)' }}> · keeping extra columns: {preview.unmapped.join(', ')}</span>}
                    </div>
                    <div style={{ maxHeight: 190, overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <caption className="cw-sr-only">Preview of the first rows to import</caption>
                        <thead><tr><th scope="col" style={th}>Reference</th><th scope="col" style={th}>Name</th><th scope="col" style={th}>Type</th><th scope="col" style={th}>Location</th></tr></thead>
                        <tbody>
                          {preview.rows.slice(0, 25).map((r, i) => (
                            <tr key={i}>
                              <td style={{ ...td, ...mono, color: r.asset_ref ? 'var(--cw-text)' : 'var(--cw-danger-fg)' }}>
                                {r.asset_ref || 'Missing — will be skipped'}
                              </td>
                              <td style={td}>{r.name || '—'}</td>
                              <td style={td}>{r.asset_type || '—'}</td>
                              <td style={td}>{r.location || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {preview.rows.length > 25 && (
                      <div className="cw-meta" style={{ padding: '7px 12px', borderTop: '1px solid var(--cw-border-light)' }}>
                        …and {preview.rows.length - 25} more. All {preview.rows.length} will be imported.
                      </div>
                    )}
                  </div>
                ) : (
                  <div role="alert" style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-warning-fg)', background: 'var(--cw-warning-tint)', border: '1px solid var(--cw-warning-border)', borderRadius: 8, padding: '9px 12px' }}>
                    Nothing recognisable in that paste. Each row needs at least a reference or a name.
                  </div>
                )
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

/** Footer Cancel that respects the modal's dirty guard. */
const CancelButton = ({ disabled }) => {
  const requestClose = useModalClose();
  return <button type="button" className="cw-btn cw-btn-secondary" onClick={requestClose} disabled={disabled}>Cancel</button>;
};

export default AssetRegister;
